import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api/client";

export interface ApiState<T> {
  data: T | null;
  loading: boolean;
  error: ApiError | Error | null;
  refetch: () => void;
}

/** Tiny GET hook. No caching, no retry — this is internal tooling, simple is fine. */
export function useApi<T>(path: string | null, deps: unknown[] = []): ApiState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(path !== null);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const tick = useRef(0);

  const refetch = () => { tick.current += 1; setLoading(true); setError(null); load(); };

  function load() {
    if (path === null) { setLoading(false); return; }
    const myTick = tick.current;
    api.get<T>(path)
      .then((d) => { if (myTick === tick.current) { setData(d); setError(null); } })
      .catch((e) => { if (myTick === tick.current) setError(e); })
      .finally(() => { if (myTick === tick.current) setLoading(false); });
  }

  useEffect(() => {
    if (path === null) { setLoading(false); setData(null); return; }
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...deps]);

  return { data, loading, error, refetch };
}
